import { useEffect, useState } from "react";
import { Plus, Trash2, AlertTriangle, Search } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import SearchableSelect from "../../components/SearchableSelect";
import DocumentScannerBox from "../../components/DocumentScannerBox";

const TIPI = ["carrozzeria", "meccanico", "pneumatici", "cerchi", "vetri", "interni", "luci", "sottoscocca", "altro"];
const GRAVITA = {
  lieve: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  medio: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  grave: "bg-[#ff0033]/15 text-[#ff5577] border-[#ff0033]/40",
};
const STATO = {
  aperto: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  in_lavorazione: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  risolto: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  archiviato: "bg-white/10 text-white/60 border-white/20",
};

export default function Damages() {
  const [list, setList] = useState([]);
  const [cars, setCars] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pageSearch, setPageSearch] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    car_id: "", contract_id: "", customer_id: "", tipo: "carrozzeria",
    descrizione: "", gravita: "lieve", costo_stimato: 0, addebitato_cliente: false,
    stato: "aperto", note: "", data_rilevazione: new Date().toISOString().slice(0, 10),
  });

  const load = async () => {
    const [d, c, ct, cu] = await Promise.all([api.get("/damages"), api.get("/cars"), api.get("/contracts"), api.get("/customers")]);
    setList(d.data); setCars(c.data); setContracts(ct.data); setCustomers(cu.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const ct = contracts.find((c) => c.id === form.contract_id);
      const payload = { ...form, costo_stimato: +form.costo_stimato, customer_id: ct?.customer_id || form.customer_id || null };
      await api.post("/damages", payload);
      toast.success("Danno registrato");
      setShow(false); load();
    } catch (e) { toast.error("Errore"); }
  };

  const updateStato = async (d, stato) => {
    await api.put(`/damages/${d.id}`, { ...d, stato });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/damages/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="damages-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Gestione Danni</h1>
          <p className="text-sm text-white/45 mt-1">{list.length} danni · {list.filter(d => d.stato === 'aperto').length} aperti</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input data-testid="damage-page-search" placeholder="Cerca cliente, targa, contratto…" value={pageSearch} onChange={(e) => setPageSearch(e.target.value)}
              className="bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm w-72 focus:border-[#ff0033]/60 focus:outline-none" />
          </div>
          <button data-testid="damage-add" onClick={() => setShow(true)} className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
            <Plus size={14} /> Nuovo Danno
          </button>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left p-4">Veicolo</th>
              <th className="text-left p-4">Tipo · Descrizione</th>
              <th className="p-4">Gravità</th>
              <th className="p-4">Costo</th>
              <th className="p-4">Addebito</th>
              <th className="p-4">Stato</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.filter((d) => {
              if (!pageSearch) return true;
              const s = pageSearch.toLowerCase();
              const custName = (d.customer?.ragione_sociale || `${d.customer?.nome || ''} ${d.customer?.cognome || ''}`).toLowerCase();
              return [d.car?.targa, d.car?.marca, d.car?.modello, d.contract?.numero, custName, d.descrizione, d.tipo].some((x) => (x || '').toLowerCase().includes(s));
            }).map((d) => (
              <tr key={d.id} className="hover:bg-white/[0.02]" data-testid={`damage-${d.id}`}>
                <td className="p-4">
                  <div className="font-medium">{d.car?.marca} {d.car?.modello}</div>
                  <div className="text-[10px] font-mono text-white/40">{d.car?.targa}</div>
                </td>
                <td className="p-4">
                  <div className="text-xs uppercase tracking-wider text-white/40">{d.tipo}</div>
                  <div className="text-sm truncate max-w-md">{d.descrizione}</div>
                  {d.contract && <div className="text-[10px] text-white/40 font-mono">Contratto {d.contract.numero}</div>}
                  {d.customer && <div className="text-[10px] text-emerald-300/80">{d.customer.ragione_sociale || `${d.customer.nome || ''} ${d.customer.cognome || ''}`}</div>}
                </td>
                <td className="p-4 text-center">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${GRAVITA[d.gravita]}`}>{d.gravita}</span>
                </td>
                <td className="p-4 text-right">
                  <div className="font-display font-bold">€{d.costo_finale || d.costo_stimato}</div>
                  <div className="text-[10px] text-white/40">{d.costo_finale ? "finale" : "stimato"}</div>
                </td>
                <td className="p-4 text-center">
                  {d.addebitato_cliente
                    ? <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-[#ff0033]/15 text-[#ff5577] border border-[#ff0033]/40">Cliente</span>
                    : <span className="text-[10px] text-white/40">No</span>}
                </td>
                <td className="p-4 text-center">
                  <select value={d.stato} onChange={(e) => updateStato(d, e.target.value)}
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${STATO[d.stato]} bg-transparent`}>
                    {["aperto", "in_lavorazione", "risolto", "archiviato"].map((s) => <option key={s} className="bg-[#0a0a0a]">{s.replace('_', ' ')}</option>)}
                  </select>
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => remove(d.id)} className="p-2 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]">
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-white/40">Nessun danno registrato.</td></tr>}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-xl glass-strong rounded-3xl p-6 max-h-[92vh] overflow-auto" data-testid="damage-form">
            <div className="font-display text-2xl font-bold mb-5">Nuovo Danno</div>

            {/* SCANNER AI PERIZIA / PREVENTIVO */}
            <div className="mb-5">
              <DocumentScannerBox context="damages"
                testIdPrefix="damage-scan"
                title="Scansione AI · Perizia / Preventivo"
                subtitle="→ Auto-compila Targa + Costo stimato"
                description="Carica foto o PDF di perizia carrozziere, preventivo riparazione, foto del danno. L'AI estrae targa veicolo, descrizione danno e costo stimato."
                variant="compact"
                onResult={(payload) => {
                  const v = payload?.veicolo || {};
                  const doc = payload?.documento || {};
                  setForm((f) => ({
                    ...f,
                    descrizione: payload?.note || doc.descrizione || f.descrizione,
                    costo_stimato: (typeof payload.importo === "number" ? payload.importo : f.costo_stimato) || f.costo_stimato,
                  }));
                  if (v.targa) {
                    const tg = v.targa.toUpperCase();
                    const match = cars.find((c) => (c.targa || "").toUpperCase() === tg);
                    if (match) {
                      setForm((f) => ({ ...f, car_id: match.id }));
                      toast.success(`Veicolo ${tg} associato dalla flotta`);
                    }
                  }
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* === CLIENTE SEARCH (cross-link contratti) === */}
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Cliente (cerca per nome, CF, P.IVA)</label>
                <SearchableSelect
                  testid="damage-cust"
                  value={form.customer_id}
                  onChange={(v) => setForm({ ...form, customer_id: v, contract_id: "" })}
                  placeholder="Digita nome, cognome, ragione sociale, CF o P.IVA…"
                  options={customers.map((c) => ({
                    value: c.id,
                    label: c.ragione_sociale || `${c.nome || ""} ${c.cognome || ""}`.trim() || "(senza nome)",
                    extra: c.codice_fiscale || c.piva || c.email || "",
                  }))}
                />
                {form.customer_id && (() => {
                  const c = customers.find((x) => x.id === form.customer_id);
                  if (!c) return null;
                  const custContracts = contracts.filter((ct) => ct.customer_id === form.customer_id);
                  return (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-emerald-500/5 border border-emerald-500/30 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="font-medium">{c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`}</div>
                          <div className="text-[10px] text-white/45 font-mono">{c.codice_fiscale || c.piva || '—'}</div>
                        </div>
                        <button type="button" data-testid="damage-cust-clear" onClick={() => setForm({ ...form, customer_id: "", contract_id: "" })}
                          className="text-[10px] text-white/40 hover:text-[#ff0033]">✕ rimuovi</button>
                      </div>
                      {custContracts.length > 0 && (
                        <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-300/80">{custContracts.length} contratti collegati</div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Veicolo</label>
                <select required value={form.car_id} onChange={(e) => setForm({ ...form, car_id: e.target.value })}
                  className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">—</option>
                  {cars.map((c) => <option key={c.id} value={c.id}>{c.marca} {c.modello} · {c.targa}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Contratto associato {form.customer_id ? "(solo del cliente selezionato)" : "(opzionale)"}</label>
                <select value={form.contract_id} onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  data-testid="damage-contract-select"
                  className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">— Nessuno</option>
                  {contracts
                    .filter((c) => !form.car_id || c.car_id === form.car_id)
                    .filter((c) => !form.customer_id || c.customer_id === form.customer_id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c.numero} · {c.customer?.ragione_sociale || `${c.customer?.nome||''} ${c.customer?.cognome||''}`}</option>
                    ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Tipo</label>
                <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {TIPI.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Gravità</label>
                <select value={form.gravita} onChange={(e) => setForm({ ...form, gravita: e.target.value })}
                  className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {["lieve", "medio", "grave"].map((g) => <option key={g}>{g}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Descrizione</label>
                <textarea required rows={2} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Costo stimato €</label>
                <input type="number" step="0.01" value={form.costo_stimato} onChange={(e) => setForm({ ...form, costo_stimato: e.target.value })}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Data rilevazione</label>
                <input type="date" value={form.data_rilevazione} onChange={(e) => setForm({ ...form, data_rilevazione: e.target.value })}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <label className="col-span-2 flex items-center gap-2 text-sm py-2">
                <input type="checkbox" checked={form.addebitato_cliente} onChange={(e) => setForm({ ...form, addebitato_cliente: e.target.checked })} />
                <span>Addebitare al cliente del contratto associato</span>
              </label>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button data-testid="damage-form-submit" type="submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">Registra</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
